// Shipping/TNT arrival-date API and client-location broker used by the
// ShippingLocalization hook/components, OrderDetail, and the Casper-compare
// shipping-estimator decorator (ui/compareLocalization.ts).
import { EventEmitter } from "events";
import * as location from "tsi-common-react/src/api/location";
import { ILocationGuessResponse } from "tsi-common-react/src/models/location";
import { fetchJson } from "tsi-common-react/src/utils/ajax";

import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";

interface TNTResponse {
    city: string;
    state_province_code: string;
    country_code: string;
    postal_code: string;
    shipping_cost: string;
    state_name: string;
    location_name: string;
    days_in_transit: number;
    arrives_on?: Date;
}

const arrivalCache: { [s: string]: TNTResponse } = {};
const arrivalRequests: { [s: string]: Promise<TNTResponse | null> } = {};
let clientLocationRequest: Promise<ILocationGuessResponse | null> | null = null;
let pendingClientZip: string | null = null;

const storageObj: { [s: string]: string } = (() => {
    const window = getWindow();
    const isStorageSupported = () => {
        if (!window || !window.sessionStorage) {
            return false;
        }
        try {
            window.sessionStorage.setItem("test", "test");
            window.sessionStorage.removeItem("test");
            return true;
        } catch (e) {
            return false;
        }
    };
    return isStorageSupported() && window ? window.sessionStorage : {};
})();

const getPreferredClientZip = (): string | null =>
    storageObj["shipping-client-zip"] || pendingClientZip;

class ShippingCalc extends EventEmitter {
    public readonly getClientLocation =
        async (): Promise<ILocationGuessResponse | null> => {
            const preferredZip = getPreferredClientZip();
            if (preferredZip) {
                return {
                    zip: preferredZip,
                };
            }
            if (!clientLocationRequest) {
                clientLocationRequest = location
                    .guess()
                    .then((l) => {
                        const preferredZip = getPreferredClientZip();
                        if (preferredZip) {
                            return {
                                zip: preferredZip,
                            };
                        }
                        this.emit("change", l);
                        return l;
                    })
                    .catch(() => {
                        // Allow a retry on the next call instead of
                        // permanently caching the failed result.
                        clientLocationRequest = null;
                        return null;
                    });
            }
            return clientLocationRequest;
        };

    public readonly setPendingClientLocation = (zip: string) => {
        pendingClientZip = zip;
    };

    public readonly clearPendingClientLocation = (zip: string) => {
        if (pendingClientZip === zip) {
            pendingClientZip = null;
        }
    };

    public readonly setClientLocation = (zip: string) => {
        this.clearPendingClientLocation(zip);
        storageObj["shipping-client-zip"] = zip;
        eventBroker.emit("change", {
            zip: zip,
        });
    };

    public readonly getShipmentArrivalDate = async (
        postalCode: string,
        shipDate?: string,
    ): Promise<TNTResponse | null> => {
        const cacheKey = `${postalCode}:${shipDate ?? ""}`;
        if (!arrivalCache[cacheKey]) {
            const params = new URLSearchParams({ postal_code: postalCode });
            if (shipDate) {
                params.append("ship_date", shipDate);
            }
            if (!arrivalRequests[cacheKey]) {
                arrivalRequests[cacheKey] = fetchJson<
                    TNTResponse & { arrives_on: string }
                >(`/api/tnt/?${params}`)
                    .then((body) => {
                        arrivalCache[cacheKey] = {
                            ...body,
                            arrives_on: new Date(body.arrives_on),
                        };
                        return arrivalCache[cacheKey];
                    })
                    .catch((e) => {
                        console.error(e);
                        return null;
                    })
                    .finally(() => {
                        delete arrivalRequests[cacheKey];
                    });
            }
            return arrivalRequests[cacheKey];
        }
        return arrivalCache[cacheKey] || null;
    };
}

const eventBroker = new ShippingCalc();
export default eventBroker;
